import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { DjaySpecimen, useDjaySelection } from '../../mascot';
import { VARIANT_META, type MascotRarity } from '../../mascot/useVariantUnlocks';
import { useSkinReveal } from '../../mascot/unlock/useSkinReveal';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
/**
 * SkinEvolutionReveal — a cinematic, Pokémon-style "evolution" sequence for a
 * newly unlocked Djay look. Plays in three beats:
 *
 *   1. CHARGE  — current (base) Djay pulses, energy gathers, screen darkens.
 *   2. FLASH   — a white-out silhouette swap: the old look bleaches to a
 *                silhouette, the new look bleaches in, then colour returns.
 *   3. REVEAL  — the new look bursts out with rays + confetti, its name +
 *                rarity, and an "Aandoen / Verder" choice.
 *
 * Mounted once globally (AppShell). Any caller — in-session OR the standalone
 * `DJ Goud` long-press easter egg — fires it via `triggerSkinReveal(...)`.
 */
type Phase = 'charge' | 'flash' | 'reveal';
const RARITY_RAYS: Record<MascotRarity, string> = {
  common: '#cbd5e1',
  uncommon: '#34d399',
  rare: '#60a5fa',
  legendary: '#fbbf24',
  secret: '#facc15'
};
const RARITY_LABEL: Record<MascotRarity, string> = {
  common: 'Gewoon',
  uncommon: 'Bijzonder',
  rare: 'Zeldzaam',
  legendary: 'Legendarisch!',
  secret: 'Geheim!'
};
export function SkinEvolutionReveal() {
  const {
    active,
    dismiss
  } = useSkinReveal();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    setSelectedVariant
  } = useDjaySelection();
  const [phase, setPhase] = useState<Phase>('charge');
  const meta = active ? VARIANT_META[active.variant] : null;
  const rayColor = meta ? RARITY_RAYS[meta.rarity] : RARITY_RAYS.legendary;
  // Drive the cinematic timeline. Reduced-motion users skip straight to the
  // reveal so the experience never traps them in a long animation.
  useEffect(() => {
    if (!active) return;
    setPhase('charge');
    if (!shouldAnimate) {
      setPhase('reveal');
      playSoundIntent('reward.achievement');
      hapticManager.hapticCelebration();
      return;
    }
    playSoundIntent('reward.levelUp');
    hapticManager.hapticHeavy();
    const toFlash = setTimeout(() => {
      setPhase('flash');
      hapticManager.hapticHeavy();
    }, 1400);
    const toReveal = setTimeout(() => {
      setPhase('reveal');
      playSoundIntent('reward.achievement');
      hapticManager.hapticCelebration();
    }, 2300);
    return () => {
      clearTimeout(toFlash);
      clearTimeout(toReveal);
    };
  }, [active, shouldAnimate]);
  const rays = useMemo(() => Array.from({
    length: 12
  }, (_, i) => i / 12 * 360), []);
  if (!active || !meta) return null;
  const fromVariant = active.from ?? 'default';
  const handleEquip = () => {
    setSelectedVariant(active.variant);
    try {
      sessionStorage.setItem('collectionFocus', active.variant);
    } catch {
      /* sessionStorage unavailable — non-critical */
    }
    dismiss();
  };
  return <AnimatePresence data-id="element-2549" data-mp="2gdpnfzvcv5hj">
      <div role="dialog" aria-modal="true" aria-label={`Nieuwe look ontgrendeld: ${meta.name}`} className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-id="element-2550" data-mp="2ge91v6lsrbqt">
        {/* Darkening backdrop */}
        <motion.div initial={shouldAnimate ? {
        opacity: 0
      } : false} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-950/85 backdrop-blur-md" onClick={phase === 'reveal' ? dismiss : undefined} data-id="element-2551" data-mp="2pd2vqp7qto0o" />

        {/* Rotating ray burst — intensifies through the sequence */}
        <motion.div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none" animate={shouldAnimate ? {
        rotate: 360,
        opacity: phase === 'reveal' ? 0.9 : 0.4
      } : {
        opacity: 0.6
      }} transition={shouldAnimate ? {
        rotate: {
          duration: 16,
          ease: 'linear',
          repeat: Infinity
        }
      } : undefined} data-id="element-2552" data-mp="38km5h0ims5j">
          <div className="relative w-[140vmin] h-[140vmin]" data-id="element-2553" data-mp="31pepq131tlts">
            {rays.map((deg, i) => <div key={i} className="absolute left-1/2 top-1/2 origin-bottom" style={{
            width: '6vmin',
            height: '70vmin',
            transform: `translate(-50%, -100%) rotate(${deg}deg)`,
            background: `linear-gradient(to top, ${rayColor}00, ${rayColor}55)`,
            opacity: i % 2 === 0 ? 1 : 0.5
          }} data-id="element-2554" data-mp="2xod08byxoo50" data-mpi={i} />)}
          </div>
        </motion.div>

        {/* Confetti only on the final reveal */}
        {phase === 'reveal' && <div className="absolute inset-0 pointer-events-none z-[1]" data-id="element-2555" data-mp="28v9wgshrh6yd">
            <ConfettiCelebration data-id="element-2556" data-mp="1uupbpqsad128" />
          </div>}

        {/* Stage */}
        <div className="relative z-10 flex flex-col items-center text-center max-h-[92vh]" data-id="element-2557" data-mp="1uwsdt96pqknb">
          {/* Eyebrow */}
          <motion.p initial={shouldAnimate ? {
          opacity: 0,
          y: -8
        } : false} animate={{
          opacity: 1,
          y: 0
        }} className="mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-black uppercase tracking-[0.2em]" data-id="element-2558" data-mp="3p8qcwibb605h">
            <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2559" data-mp="3iu1gvvnh1cr6" />
            {active.eyebrow ?? 'Nieuwe look!'}
          </motion.p>

          {/* The evolving avatar */}
          <div className="relative w-56 h-56 flex items-center justify-center" data-id="element-2560" data-mp="faoo49wfgdmo">
            {/* FROM look — base Djay, present during charge + flash */}
            <motion.div className="absolute inset-0 flex items-center justify-center" animate={shouldAnimate ? {
            opacity: phase === 'reveal' ? 0 : 1,
            scale: phase === 'charge' ? [1, 1.06, 1] : phase === 'flash' ? 0.85 : 1,
            x: phase === 'charge' ? [0, -3, 3, -2, 2, 0] : 0,
            filter: phase === 'flash' ? 'brightness(4) saturate(0)' : 'brightness(1)'
          } : {
            opacity: phase === 'reveal' ? 0 : 1
          }} transition={shouldAnimate ? phase === 'charge' ? {
            duration: 1.1,
            repeat: Infinity,
            ease: 'easeInOut'
          } : {
            duration: 0.45,
            ease: 'easeInOut'
          } : undefined} data-id="element-2561" data-mp="5kwtjfobvki7">
              <DjaySpecimen size="hero" emotion="thinking" look={{
              variant: fromVariant
            }} animate={false} className="w-44 h-44" data-id="element-2562" data-mp="27bvxbr0xhgt3" />
            </motion.div>

            {/* TO look — the new variant, bleaches in then colourises */}
            <motion.div className="absolute inset-0 flex items-center justify-center" initial={shouldAnimate ? {
            opacity: 0,
            scale: 0.6
          } : false} animate={shouldAnimate ? {
            opacity: phase === 'charge' ? 0 : 1,
            scale: phase === 'reveal' ? 1 : 0.85,
            filter: phase === 'flash' ? 'brightness(4) saturate(0)' : 'brightness(1) saturate(1)'
          } : {
            opacity: phase === 'reveal' ? 1 : 0
          }} transition={shouldAnimate ? phase === 'reveal' ? {
            type: 'spring',
            stiffness: 200,
            damping: 13
          } : {
            duration: 0.4,
            ease: 'easeOut'
          } : undefined} data-id="element-2563" data-mp="2xkvzm98ug8kt">
              <DjaySpecimen size="hero" emotion="celebrating" look={{
              variant: active.variant
            }} animate={shouldAnimate && phase === 'reveal'} className="w-44 h-44" data-id="element-2564" data-mp="pb69ubojhf22" />
            </motion.div>

            {/* White flash overlay during the swap */}
            {shouldAnimate && <motion.div aria-hidden="true" className="absolute inset-0 rounded-full bg-white pointer-events-none" initial={{
            opacity: 0,
            scale: 0.4
          }} animate={{
            opacity: phase === 'flash' ? [0, 0.95, 0] : 0,
            scale: phase === 'flash' ? [0.4, 1.4, 1.6] : 0.4
          }} transition={{
            duration: 0.9,
            ease: 'easeInOut'
          }} data-id="element-2565" data-mp="310gpclfg1l89" />}
          </div>

          {/* Reveal copy + actions */}
          <AnimatePresence data-id="element-2566" data-mp="5ka0frs01o9k">
            {phase === 'reveal' && <motion.div key="reveal-copy" initial={shouldAnimate ? {
            opacity: 0,
            y: 16
          } : false} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mt-6 flex flex-col items-center" data-id="element-2567" data-mp="ailao3m7i0sb">
                <h2 className="text-3xl font-black text-white drop-shadow-lg" data-id="element-2568" data-mp="2nt4iev2z6iwg">
                  {meta.name}
                </h2>
                <span className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider" style={{
              backgroundColor: `${rayColor}22`,
              color: rayColor,
              border: `1px solid ${rayColor}66`
            }} data-id="element-2569" data-mp="2j0laadn24fz1">
                  <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2570" data-mp="32pvjr9w7m4op" />
                  {RARITY_LABEL[meta.rarity]}
                </span>
                <p className="mt-3 max-w-xs text-sm text-white/70" data-id="element-2571" data-mp="1cfse6j3azy9r">
                  Djay is geëvolueerd naar een gloednieuwe look. Wil je 'm
                  meteen aandoen?
                </p>

                <div className="mt-6 flex w-full max-w-xs gap-3" data-id="element-2572" data-mp="37iyufifplh01">
                  <Button onClick={dismiss} variant="outline" size="lg" className="flex-1 font-bold bg-white/5 border-white/30 text-white hover:bg-white/10" data-id="element-2573" data-mp="3cgqke6wsmt1l">
                    Later
                  </Button>
                  <Button onClick={handleEquip} size="lg" className="flex-1 font-bold" data-id="element-2574" data-mp="3qf718qoddzxg">
                    <CheckIcon className="w-4 h-4 mr-2" aria-hidden="true" data-id="element-2575" data-mp="3zvqvi1vyl06" />
                    Aandoen
                  </Button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </AnimatePresence>;
}